import { useState, useCallback, useMemo } from 'react';
import { useUrlSyncedId } from '../../hooks/useUrlSyncedId';
import { Box, Flex, IconButton, Spinner, Center, Text, VStack } from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { LuLayers } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { useAccountsOverview } from '../../hooks/useAccountsOverview';
import useAccountFreshness from '../../hooks/useAccountFreshness';
import useHoldingReturns from '../../hooks/useHoldingReturns';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import AccountsOverviewCard from './AccountsOverviewCard';
import AccountsList from './AccountsList';
import AccountsDisplayMenu from './AccountsDisplayMenu';
import { groupAccounts, readGroupBy, saveGroupBy } from '../../utils/accountGrouping';
import AccountDetailPanel from './AccountDetailPanel';
import DashboardCurrencyTabs from '../dashboard/DashboardCurrencyTabs';
import DisplayCurrencySelector from '../shared/DisplayCurrencySelector';
import OnboardingEmptyState from '../onboarding/OnboardingEmptyState';
import UnconvertibleNote from '../shared/UnconvertibleNote';
import PageHeader from '../shared/PageHeader';
import { TONE } from './tones';

import { useResizablePanel } from '../../hooks/useResizablePanel';
import PanelResizeHandle, { pagePanelOptions, resizablePanelWidth } from '../shared/PanelResizeHandle';
import useIsPhone from '../../hooks/useIsPhone';
import PhoneAccounts from '../phone/PhoneAccounts';
const PANEL_CREATE_MODE = 'new';

// The phone gets its own page rather than a responsive branch inside this
// one, so none of the desktop body's hooks or fetches run there.
export default function AccountsPage(props) {
  return useIsPhone() ? <PhoneAccounts {...props} /> : <AccountsPageDesktop {...props} />;
}

function AccountsPageDesktop() {
  const { t } = useTranslation('accounts');
  const { can } = useWorkspace();
  const canCreate = can('account.create');
  const canUpdate = can('account.update');
  // Each row says how far its statements go, as the Statements page does.
  const freshness = useAccountFreshness(can('source_file.read'));
  const {
    loading, error, refetch,
    range, setRange,
    currency, setCurrency,
    availableCurrencies,
    accounts, activeAccounts,
    summary, debtSummary, convertedSummary, convertedAccountStats, convertedIsConverted,
    convertedRoiByAccount, displayCurrency, usdRates, rateSources, trend, chartPeriods, granularity,
    accountsByGroup, accountStats, perCurrencyAccountStats, roiByAccount,
    categories, allTags,
    buckets,
    balanceBuckets,
  } = useAccountsOverview();

  // A holding's return in the display currency since purchase, each buy at
  // its own day's rate: the list's % pill reads it in place of its own.
  const listReturns = useHoldingReturns(convertedAccountStats ? displayCurrency : null, null, accounts);

  const [selectedAccountId, setSelectedAccountId] = useUrlSyncedId('account_id');
  const [currencyTab, setCurrencyTab] = useState('all');
  const [groupBy, setGroupBy] = useState(readGroupBy);
  const handleGroupBy = useCallback((value) => {
    setGroupBy(value);
    saveGroupBy(value);
  }, []);

  // "Todas" with >1 currency and no active conversion → the multi-currency view:
  // empty chart, per-currency cards, per-currency rows. A single-currency
  // workspace or a converted view keeps the single-figure layout unchanged.
  const isMultiCurrencyView = !displayCurrency && currencyTab === 'all' && availableCurrencies.length > 1;

  // Balances are valued at today's rate, so a standard currency that can't
  // convert is missing *today's* rate specifically — name that day (YYYY-MM-DD).
  const today = new Date();
  const todayYMD = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const showUnconvertible = Boolean(displayCurrency) && convertedSummary?.num_unconvertible > 0;

  const handleCurrencyChange = useCallback((c) => {
    setCurrencyTab(c);
    setCurrency(c === 'all' ? null : c);
  }, [setCurrency]);

  const groupedAccounts = useMemo(
    () => groupAccounts(groupBy, accounts || [], accountsByGroup),
    [groupBy, accounts, accountsByGroup],
  );

  const filteredAccountsByGroup = useMemo(() => {
    if (currencyTab === 'all') return groupedAccounts;
    const ids = new Set(buckets.filter(b => b.currency === currencyTab).map(b => b.account_id));
    const result = {};
    Object.entries(groupedAccounts).forEach(([group, accs]) => {
      const filtered = accs.filter(a => ids.has(a.id) || a.currencies?.includes(currencyTab));
      if (filtered.length > 0) result[group] = filtered;
    });
    return result;
  }, [currencyTab, groupedAccounts, buckets]);

  const isPanelOpen = selectedAccountId !== null;
  // Same drag-to-size panel as the Movements page.
  const panelResize = useResizablePanel(isPanelOpen, pagePanelOptions(440));
  // The account form is a narrow column (MI-340); an account's detail is a
  // chart plus its movements, which wants the page's usual share. The panel
  // reports when it swaps to the form so the width follows.
  const [isShowingForm, setIsShowingForm] = useState(false);

  const handleAccountClick = useCallback((account) => {
    setSelectedAccountId(account.id);
  }, []);

  const handleCreateClick = useCallback(() => {
    setSelectedAccountId(PANEL_CREATE_MODE);
  }, []);

  const handleClosePanel = useCallback(() => {
    setSelectedAccountId(null);
    // The panel unmounts without reporting, so clear the form width here.
    setIsShowingForm(false);
  }, []);

  const handleAccountUpdated = useCallback(() => {
    refetch();
  }, [refetch]);

  return (
    <Flex ref={panelResize.containerRef} h={{ base: 'calc(100vh - 56px)', md: 'var(--mk-page-h)' }} overflow="hidden" position="relative">
      {/* Left: Overview + list */}
      <Box flex={1} display="flex" flexDirection="column" minW={0}>
        {/* Header */}
        <PageHeader title={t('title')}>
            {/* MI-298: global "display in" control (stock view, today's rate) */}
            <DisplayCurrencySelector />
            {availableCurrencies.length > 1 && (
              <DashboardCurrencyTabs
                currencies={availableCurrencies}
                selected={currencyTab}
                onChange={handleCurrencyChange}
              />
            )}
            <AccountsDisplayMenu groupBy={groupBy} onGroupByChange={handleGroupBy} />
            {canCreate && (
              <IconButton
                icon={<AddIcon />}
                onClick={handleCreateClick}
                aria-label={t('newAria')}
                size="sm"
                variant="ghost"
                color="redesign.text.muted"
                _hover={{ color: 'redesign.heading', bg: 'redesign.bg.card' }}
              />
            )}
        </PageHeader>

        {/* Scrollable content */}
        <Box flex={1} overflowY="auto" px={{ base: 3, md: 5 }} py={5}>
          {loading ? (
            <Center py={20}>
              <VStack spacing={4}>
                <Spinner size="xl" color="redesign.text.muted" />
                <Text color="redesign.text.muted">{t('loading')}</Text>
              </VStack>
            </Center>
          ) : error ? (
            <Center py={20}>
              <Text color="redesign.accent.red">{error}</Text>
            </Center>
          ) : (accounts?.length ?? 0) === 0 ? (
            <OnboardingEmptyState
              titleKey="empty.accounts.title"
              subtitleKey="empty.accounts.subtitle"
              actions={canCreate ? [
                { label: 'empty.accounts.create', primary: true, onClick: handleCreateClick },
              ] : []}
            />
          ) : (
            <>
              {/* MI-298: balances excluded from the ≈ totals are disclosed here —
                  centered, in the gap above the card, boxed per the MI-298 mock. */}
              {showUnconvertible && (
                <Flex justify="center" mb={4}>
                  <Box
                    border="1px solid"
                    borderColor="redesign.accent.amber"
                    {...TONE.warn}
                    borderRadius="md"
                    px={4}
                    py={2}
                  >
                    <UnconvertibleNote
                      buckets={convertedSummary.unconvertible}
                      count={convertedSummary.num_unconvertible}
                      variant="balances"
                      date={todayYMD}
                      justify="center"
                    />
                  </Box>
                </Flex>
              )}
              <AccountsOverviewCard
                summary={summary}
                debtSummary={debtSummary}
                converted={convertedSummary}
                convertedIsConverted={convertedIsConverted}
                displayCurrency={displayCurrency}
                multiCurrency={isMultiCurrencyView}
                trend={trend}
                range={range}
                onRangeChange={setRange}
                currency={currency}
                chartPeriods={chartPeriods}
                granularity={granularity}
                accounts={activeAccounts}
              />
              <AccountsList
                accountsByGroup={filteredAccountsByGroup}
                groupBy={groupBy}
                accountStats={convertedAccountStats || accountStats}
                roiByAccount={convertedRoiByAccount || roiByAccount}
                currency={convertedAccountStats ? displayCurrency : currency}
                approx={Boolean(convertedAccountStats)}
                convert={convertedAccountStats
                  ? {
                    target: displayCurrency, rates: usdRates, sources: rateSources,
                    returns: listReturns?.holdings, brokerReturns: listReturns?.brokers,
                  }
                  : null}
                multiCurrency={isMultiCurrencyView}
                perCurrencyAccountStats={perCurrencyAccountStats}
                selectedAccountId={selectedAccountId}
                onAccountClick={handleAccountClick}
                freshness={freshness}
              />
            </>
          )}
        </Box>
      </Box>

      {/* Right: Detail panel (always visible on md+) */}
      <Box
        display={{ base: isPanelOpen ? 'block' : 'none', md: 'block' }}
        position={{ base: 'absolute', md: 'relative' }}
        right={{ base: 0, md: 'auto' }}
        top={{ base: 0, md: 'auto' }}
        w={isShowingForm
          ? { base: '100%', md: resizablePanelWidth('520px', 440) }
          : { base: '100%', md: resizablePanelWidth('44%', 440), lg: resizablePanelWidth('42%', 440) }}
        h="100%"
        bg="redesign.bg.surface"
        borderLeft="1px solid"
        borderLeftColor="redesign.border"
        boxShadow={{ base: '-2px 0 8px rgba(0,0,0,0.15)', md: 'none' }}
        zIndex={{ base: 10, md: 'auto' }}
      >
        <PanelResizeHandle resize={panelResize} />
        {isPanelOpen ? (
          <AccountDetailPanel
            accountId={selectedAccountId === PANEL_CREATE_MODE ? null : selectedAccountId}
            onClose={handleClosePanel}
            onAccountUpdated={canUpdate ? handleAccountUpdated : undefined}
            onEditingChange={setIsShowingForm}
            currency={currency}
            currencyTab={currencyTab}
            categories={categories}
            allTags={allTags}
            buckets={balanceBuckets}
            roi={selectedAccountId !== PANEL_CREATE_MODE ? roiByAccount?.get(selectedAccountId) : undefined}
            onSelectAccount={setSelectedAccountId}
            freshness={freshness.get(selectedAccountId)}
            paidReturns={listReturns}
          />
        ) : (
          <Flex direction="column" align="center" justify="center" gap={3} h="100%" bg="redesign.bg.page" color="redesign.text.faint">
            <Box as={LuLayers} boxSize="32px" />
            <Text fontSize="sm" color="redesign.text.muted">{t('selectToView')}</Text>
          </Flex>
        )}
      </Box>
    </Flex>
  );
}
